import { MDF_ACTIVITIES_TRANSLATION_KEYS } from '../../configs/translations'
import { IMdfActivityWithBusinessPriorities } from '../../model/types'

const KEYS = MDF_ACTIVITIES_TRANSLATION_KEYS.detailsModal

/** cng-common form state key for the details modal (holds the editable outcome). Cleared on close. */
export const MDF_ACTIVITY_DETAILS_FORM_KEY = 'mdf-activity-details'

/**
 * How a field's read-only value is rendered. `list`/`emails` render as chips;
 * `suggestions`/`date`/`currency`/`percent` get dedicated formatting.
 */
export type TMdfFieldKind =
  | 'text'
  | 'number'
  | 'percent'
  | 'currency'
  | 'date'
  | 'emails'
  | 'list'
  | 'suggestions'
  | 'textarea'

/** Kinds that span the full grid row (their values are long / multi-item). */
export const WIDE_KINDS: readonly TMdfFieldKind[] = ['list', 'emails', 'suggestions', 'textarea']

export interface IMdfFieldConfig {
  field: keyof IMdfActivityWithBusinessPriorities
  labelKey: string
  kind: TMdfFieldKind
  /** When set, field is shown only when the matching currency mode is selected. */
  currencyVariant?: 'usd' | 'lc'
}

export interface IMdfFieldGroup {
  titleKey: string
  /** Icon-font class(es) for the section-card header, e.g. `'kbase kbase-info-alt'`. */
  icon: string
  fields: IMdfFieldConfig[]
}

/**
 * "General" section (Details tab). Activity-sourced fields. `activityStatus` is
 * rendered as a `StatusBadge` and `businessUnit` as joined text by the component;
 * the rest use the kind-based read-only formatting.
 */
export const GENERAL_GROUP: IMdfFieldGroup = {
  titleKey: KEYS.groups.general,
  icon: 'kbase kbase-info-alt',
  fields: [
    { field: 'activityType', labelKey: KEYS.fields.activityType, kind: 'text' },
    { field: 'timeframe', labelKey: KEYS.fields.timeframe, kind: 'text' },
    { field: 'activityStatus', labelKey: KEYS.fields.activityStatus, kind: 'text' },
    { field: 'strategicInitiatives', labelKey: KEYS.fields.strategicInitiatives, kind: 'list' },
    { field: 'businessUnit', labelKey: KEYS.fields.businessUnit, kind: 'list' },
    { field: 'category', labelKey: KEYS.fields.category, kind: 'text' },
  ],
}

/**
 * "Financials & Contacts" tab — every field not shown on the Details tab.
 * Provisional grouping; to be refined later.
 */
export const SECONDARY_GROUPS: IMdfFieldGroup[] = [
  {
    titleKey: KEYS.groups.financials,
    icon: 'kbase kbase-chart',
    fields: [
      {
        field: 'requestedAmountUsd',
        labelKey: KEYS.fields.requestedAmount,
        kind: 'currency',
        currencyVariant: 'usd',
      },
      {
        field: 'requestedAmountLc',
        labelKey: KEYS.fields.requestedAmount,
        kind: 'currency',
        currencyVariant: 'lc',
      },
      {
        field: 'hpContributionPercent',
        labelKey: KEYS.fields.hpContributionPercent,
        kind: 'percent',
      },
      {
        field: 'claimPaidUsd',
        labelKey: KEYS.fields.claimPaid,
        kind: 'currency',
        currencyVariant: 'usd',
      },
      {
        field: 'claimPaidLc',
        labelKey: KEYS.fields.claimPaid,
        kind: 'currency',
        currencyVariant: 'lc',
      },
    ],
  },
  {
    titleKey: KEYS.groups.dates,
    icon: 'kbase kbase-calendar',
    fields: [
      { field: 'approvalDate', labelKey: KEYS.fields.approvalDate, kind: 'date' },
      { field: 'executionStartDate', labelKey: KEYS.fields.executionStartDate, kind: 'date' },
      { field: 'executionEndDate', labelKey: KEYS.fields.executionEndDate, kind: 'date' },
    ],
  },
  {
    titleKey: KEYS.groups.contacts,
    icon: 'hpicon hpicon-envelope',
    fields: [
      { field: 'partnerContactEmails', labelKey: KEYS.fields.partnerContactEmails, kind: 'emails' },
      { field: 'hpContactEmails', labelKey: KEYS.fields.hpContactEmails, kind: 'emails' },
    ],
  },
]
